import React, { useState } from "react";
import { Check, Zap, Flame, Crown, Lock } from "lucide-react";
import { Button } from "./ui/button";

const STARTER_FEATURES = [
  "Massimo 3 preventivi al mese",
  "Motore fiscale italiano di base",
  "Vista cliente interattiva + firma online",
  "PDF con dicitura \"Creato con MyQuoteFlow\"",
  { t: "Avanzamento Lavori in tempo reale", locked: true },
  { t: "Pacchetti Tier", locked: true },
  { t: "Sconti a tempo (early-bird)", locked: true },
  { t: "Codici Promo", locked: true },
];
const PRO_FEATURES = [
  "Preventivi illimitati",
  "PDF White-Label senza marchio MyQuoteFlow",
  "Avanzamento Lavori in tempo reale",
  "Pacchetti Tier & Add-on interattivi",
  "Sconti a tempo (early-bird)",
  "Codici Promo personalizzati",
  "Riepilogo Acconto/Saldo per il cliente",
  "Firma eIDAS + Art. 1341 c.c.",
];

function FeatureRow({ item, light }) {
  const locked = typeof item === "object" && item.locked;
  const text = typeof item === "object" ? item.t : item;
  return (
    <li className="flex items-start gap-2.5 text-sm">
      {locked
        ? <Lock style={{ width: 16, height: 16 }} className={`mt-0.5 shrink-0 ${light ? "text-slate-500" : "text-slate-300"}`} />
        : <Check style={{ width: 16, height: 16 }} className="mt-0.5 shrink-0 text-emerald-500" />}
      <span className={locked ? (light ? "text-slate-400" : "text-slate-400") : (light ? "text-slate-200" : "text-slate-600")}>
        {text}{locked && <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 align-middle">PRO</span>}
      </span>
    </li>
  );
}

const presaleLabel = (until) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(until || "")) return null;
  const d = new Date();
  const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return until > today ? until.split("-").reverse().join("/") : null;
};

export default function PricingTable({ onSelect, currentPlan, config, busyPlan, hideFree = false, founderUnlocked = false }) {
  const [annual, setAnnual] = useState(true);
  const [lifetime, setLifetime] = useState(false);
  const prices = config?.prices || { pro_monthly: 19, pro_annual: 190, founder_annual: 99, founder_lifetime: 249 };
  const seatsTotal = config?.founder_seats_total ?? 50;
  const seatsTaken = config?.founder_seats_taken ?? 0;
  const seatsLeft = config?.founder_seats_left ?? Math.max(0, seatsTotal - seatsTaken);
  const soldOut = config?.founder_sold_out ?? seatsLeft <= 0;
  const founderEnabled = (config?.founder_enabled ?? true) && !(config?.founder_private && founderUnlocked !== true);
  const presaleUntil = presaleLabel(config?.founder_annual_until);
  const pct = Math.min(100, Math.round((seatsTaken / seatsTotal) * 100));

  const btn = (plan, label, cycle) => {
    const isCurrent = currentPlan === plan;
    return (
      <Button
        data-testid={`pricing-select-${plan}`}
        disabled={isCurrent || busyPlan === plan}
        onClick={() => onSelect(plan, cycle)}
        className="w-full font-semibold mt-1"
        variant={plan === "pro" || plan === "founder" ? "default" : "outline"}
      >
        {isCurrent ? "Piano attuale" : busyPlan === plan ? "Attivazione…" : label}
      </Button>
    );
  };

  return (
    <div data-testid="pricing-table">
      <div className="flex items-center justify-center gap-3 mb-8">
        <span className={`text-sm font-medium ${!annual ? "text-slate-900" : "text-slate-400"}`}>Mensile</span>
        <button data-testid="pricing-billing-toggle" onClick={() => setAnnual((v) => !v)}
          className="relative w-12 h-6 rounded-full bg-primary transition-colors" aria-label="toggle">
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-card transition-all ${annual ? "left-6" : "left-0.5"}`} />
        </button>
        <span className={`text-sm font-medium ${annual ? "text-slate-900" : "text-slate-400"}`}>
          Annuale <span className="text-emerald-600 font-semibold">(-2 mesi)</span>
        </span>
      </div>

      <div className={`grid gap-5 items-start ${hideFree ? "md:grid-cols-2 max-w-4xl mx-auto" : "md:grid-cols-3"}`}>
        {/* Starter */}
        {!hideFree && <div data-testid="plan-card-free" className="rounded-2xl border border-slate-200 bg-card p-6 flex flex-col">
          <div className="flex items-center gap-2"><Zap style={{ width: 18, height: 18 }} className="text-slate-400" /><h3 className="font-bold text-slate-900">Starter</h3></div>
          <p className="text-sm text-slate-500 mt-1">Per iniziare gratis.</p>
          <div className="mt-4 mb-5"><span className="text-4xl font-extrabold text-slate-900">€ 0</span><span className="text-slate-400 text-sm"> /sempre</span></div>
          <ul className="space-y-2.5 flex-1">{STARTER_FEATURES.map((f, i) => <FeatureRow key={i} item={f} />)}</ul>
          <div className="mt-6">{btn("free", "Inizia gratis", "monthly")}</div>
        </div>}

        {/* Pro */}
        <div data-testid="plan-card-pro" className="rounded-2xl border-2 border-primary bg-card p-6 flex flex-col relative shadow-lg">
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-bold px-3 py-1 rounded-full bg-primary text-white">PIÙ POPOLARE</span>
          <div className="flex items-center gap-2"><Crown style={{ width: 18, height: 18 }} className="text-primary" /><h3 className="font-bold text-slate-900">Pro</h3></div>
          <p className="text-sm text-slate-500 mt-1">Tutto illimitato, white-label.</p>
          <div className="mt-4 mb-5">
            <span className="text-4xl font-extrabold text-slate-900">€ {annual ? prices.pro_annual : prices.pro_monthly}</span>
            <span className="text-slate-400 text-sm"> /{annual ? "anno" : "mese"}</span>
            {annual && <p className="text-xs text-emerald-600 font-semibold mt-1">2 mesi gratis rispetto al mensile</p>}
          </div>
          <ul className="space-y-2.5 flex-1">{PRO_FEATURES.map((f, i) => <FeatureRow key={i} item={f} />)}</ul>
          <div className="mt-6">{btn("pro", "Passa a Pro", annual ? "annual" : "monthly")}</div>
        </div>

        {/* Founder */}
        {founderEnabled && (
          <div data-testid="plan-card-founder" className="rounded-2xl border border-amber-300 bg-slate-950 text-white p-6 flex flex-col relative overflow-hidden">
            <div className="relative flex flex-col flex-1">
              <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-amber-400 text-slate-900 w-fit">
                <Flame style={{ width: 12, height: 12 }} /> SOLO PRIMI {seatsTotal} ISCRITTI
              </span>
              <div className="flex items-center gap-2 mt-3"><Crown style={{ width: 18, height: 18 }} className="text-amber-400" /><h3 className="font-bold">Offerta Fondatore</h3></div>
              <div className="mt-4 mb-2">
                {lifetime ? (
                  <><span className="text-4xl font-extrabold">€ {prices.founder_lifetime}</span><span className="text-slate-300 text-sm"> una tantum</span><p className="text-xs text-amber-300 mt-1">Lifetime Deal — nessun abbonamento, preventivi Pro per sempre</p></>
                ) : (
                  <><span className="text-4xl font-extrabold">€ {prices.founder_annual}</span><span className="text-slate-300 text-sm"> /anno per sempre</span>
                    {presaleUntil && <p data-testid="founder-presale-note" className="text-xs text-white font-semibold mt-1">Pagamento unico oggi, valido fino al {presaleUntil}</p>}
                    <p className="text-xs text-amber-300 mt-1"><span className="line-through text-slate-400">€ {prices.pro_annual}</span> prezzo bloccato a vita — risparmio 48%</p></>
                )}
              </div>
              <button data-testid="founder-lifetime-toggle" onClick={() => setLifetime((v) => !v)}
                className="text-xs text-amber-300 underline w-fit mb-4">
                {lifetime ? "← Torna a € 99/anno per sempre" : "Preferisci il Pass a Vita? € 249 una tantum →"}
              </button>
              <div className="mb-4">
                <div className="h-2 rounded-full bg-white/15 overflow-hidden">
                  <div className="h-full bg-amber-400" style={{ width: `${pct}%` }} />
                </div>
                <p className="text-xs text-amber-200 mt-1.5" data-testid="founder-progress-text">
                  ⚡ {seatsTaken} su {seatsTotal} posti Fondatore già assegnati — Restano {seatsLeft} posti
                </p>
              </div>
              <ul className="space-y-2 flex-1 text-sm">
                <li className="flex items-center gap-2"><Check style={{ width: 15, height: 15 }} className="text-amber-400" /> Tutto il Piano Pro illimitato</li>
                <li className="flex items-center gap-2"><Check style={{ width: 15, height: 15 }} className="text-amber-400" /> PDF White-Label, Tier, Sconti a tempo, Codici Promo</li>
                <li className="flex items-center gap-2"><Check style={{ width: 15, height: 15 }} className="text-amber-400" /> Avanzamento Lavori + Riepilogo Acconto/Saldo</li>
                <li className="flex items-center gap-2"><Check style={{ width: 15, height: 15 }} className="text-amber-400" /> Badge "Membro Fondatore 🏅"</li>
                <li className="flex items-center gap-2"><Check style={{ width: 15, height: 15 }} className="text-amber-400" /> Accesso prioritario ai nuovi rilasci</li>
                <li data-testid="founder-einvoice-note" className="flex items-center gap-2 text-slate-400 pl-[23px]">Fatturazione elettronica (in arrivo): add-on a parte, non inclusa</li>
              </ul>
              <div className="mt-6">
                {soldOut ? (
                  <Button disabled data-testid="founder-sold-out" className="w-full font-semibold bg-slate-700 text-slate-300">Sold Out</Button>
                ) : (
                  <Button data-testid="pricing-select-founder" disabled={currentPlan === "founder" || busyPlan === "founder"}
                    onClick={() => onSelect("founder", lifetime ? "lifetime" : "annual")}
                    className="w-full font-semibold bg-amber-400 text-slate-900 hover:bg-amber-300">
                    {currentPlan === "founder" ? "Sei Fondatore 🏅" : busyPlan === "founder" ? "Attivazione…" : "Diventa Fondatore"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
